import React, { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { ImportFiles } from '@/components/dashboard/dialog/importFiles'
import { JoinLiveMeetingDialog } from '@/components/dashboard/home-new/JoinLiveMeetingDialog'
import { createPortal } from 'react-dom'

interface EmptyStateProps {
  onCreateEmptyMeet: () => void
  projectId: string
  projectName: string
  setMeetings: (meetings: any) => void
}

export const EmptyState = ({ onCreateEmptyMeet, projectId, projectName, setMeetings }: EmptyStateProps) => {
  const [showImportDialog, setShowImportDialog] = useState(false)
  const [showJoinLiveMeetingDialog, setShowJoinLiveMeetingDialog] = useState(false)

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 border rounded-lg bg-white dark:bg-gray-800 shadow-sm mt-8 h-[80vh] relative overflow-hidden">
      {/* Meeting soundwave animation spanning full width (bottom half) */}
      <div className="absolute inset-x-0 bottom-0 h-[20%] pointer-events-none flex items-end">
        {/* Generate a series of bars with randomized amplitudes/durations to mimic a conversation */}
        <div className="w-full h-full sound-bars">
          {useMemo(
            () =>
              Array.from({ length: 100 }).map((_, idx) => {
                const variants = ['bar-soft', 'bar-quiet', 'bar-medium', 'bar-loud']
                const variant = variants[Math.floor(Math.random() * variants.length)]
                const duration = (Math.random() * 1.5 + 1).toFixed(2) // 1s–2.5s
                const delay = (Math.random() * 2).toFixed(2)
                return (
                  <span
                    key={idx}
                    className={`sound-bar ${variant}`}
                    style={{ animationDuration: `${duration}s`, animationDelay: `${delay}s` }}
                  />
                )
              }),
            []
          )}
        </div>
      </div>

      {/* Meeting icon */}
      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-6 relative z-10">
        <svg className="w-8 h-8 text-[#FF4A8E]" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M13 8.00061L17.553 5.72461C17.7054 5.64844 17.8748 5.61249 18.045 5.62015C18.2152 5.62782 18.3806 5.67885 18.5256 5.76841C18.6706 5.85797 18.7902 5.98308 18.8733 6.13188C18.9563 6.28067 18.9999 6.44822 19 6.61861V13.3826C18.9999 13.553 18.9563 13.7205 18.8733 13.8693C18.7902 14.0181 18.6706 14.1432 18.5256 14.2328C18.3806 14.3224 18.2152 14.3734 18.045 14.3811C17.8748 14.3887 17.7054 14.3528 17.553 14.2766L13 12.0006V8.00061Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M1 6C1 5.46957 1.21071 4.96086 1.58579 4.58579C1.96086 4.21071 2.46957 4 3 4H11C11.5304 4 12.0391 4.21071 12.4142 4.58579C12.7893 4.96086 13 5.46957 13 6V14C13 14.5304 12.7893 15.0391 12.4142 15.4142C12.0391 15.7893 11.5304 16 11 16H3C2.46957 16 1.96086 15.7893 1.58579 15.4142C1.21071 15.0391 1 14.5304 1 14V6Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 relative z-10">
        No meetings recorded yet
      </h3>
      <p className="text-gray-500 dark:text-gray-400 text-center max-w-md mb-6 relative z-10">
        Record and analyze your customer conversations. Add meetings manually or upload recordings to get started.
      </p>

      <div className="flex flex-col gap-4 relative z-10">
        <Button
          onClick={() => setShowJoinLiveMeetingDialog(true)}
          className="bg-[#FF4A8E] hover:bg-[#ff2c7d] text-white"
        >
          Add Meeting
        </Button>
        <Button onClick={() => setShowImportDialog(true)} variant="outline">
          Upload Recording
        </Button>
      </div>

      {showImportDialog && (
        <ImportFiles
          projectId={projectId}
          projectName={projectName}
          handleClose={() => setShowImportDialog(false)}
          setMeetings={setMeetings}
          customNote="Upload your meeting recordings as Audio or Video files to be processed. Upload up to 10 files, each up to 2GB. Acceptable file types: MP3, MP4, MP2, WAV, FLAC, PCM, M4A, Ogg, Opus, WebM."
        />
      )}

      {showJoinLiveMeetingDialog && <JoinLiveMeetingDialog handleClose={() => setShowJoinLiveMeetingDialog(false)} />}
    </div>
  )
}
